const OPTIONS_API_BASE = 'https://live1.finstrats.com';
const WS_URL = 'wss://ws.kite.trade';

let ws = null;
let connectPromise = null;
let manuallyClosed = false;
let reconnectAttempts = 0;
let reconnectTimer = null;
let authFailed = false;


const TRADING_START_MIN = 9 * 60 + 7;
const TRADING_END_MIN = 15 * 60 + 40;
const isWithinTradingWindow = () => {
    const now = new Date();
    const day = now.getDay(); // 0 = Sunday, 6 = Saturday
    if (day === 0 || day === 6) return false;
    const mins = now.getHours() * 60 + now.getMinutes();
    return mins >= TRADING_START_MIN && mins < TRADING_END_MIN;
};

const tokenRefCounts = new Map();
const tickListeners = new Map();  // instrument_token -> Set<(price) => void>
const fullTickListeners = new Map();

const fetchSession = async () => {
    const res = await fetch(`${OPTIONS_API_BASE}/portfolio/kite_ws_session`);
    const data = await res.json();
    if (!res.ok || !data.access_token) throw new Error(data.error || 'Failed to fetch Kite ws session');
    return data;
};


const parseBinaryTicks = (buffer) => {
    const dv = new DataView(buffer);
    if (dv.byteLength < 2) return [];
    const numPackets = dv.getInt16(0, false);
    let offset = 2;
    const ticks = [];
    for (let i = 0; i < numPackets; i += 1) {
        if (offset + 2 > dv.byteLength) break;
        const packetLength = dv.getInt16(offset, false);
        offset += 2;
        if (offset + packetLength > dv.byteLength) break;
        if (packetLength >= 8) {
            const instrument_token = dv.getInt32(offset, false);
            const last_price = dv.getInt32(offset + 4, false) / 100;
            const tick = { instrument_token, last_price };
            if (packetLength >= 44) {
                tick.average_traded_price = dv.getInt32(offset + 12, false) / 100;
                tick.volume_traded = dv.getUint32(offset + 16, false);
            }
            if (packetLength >= 52) {
                tick.oi = dv.getUint32(offset + 48, false);
            }
            ticks.push(tick);
        }
        offset += packetLength;
    }
    return ticks;
};

const dispatchTick = (tick) => {
    const listeners = tickListeners.get(tick.instrument_token);
    if (listeners) listeners.forEach((cb) => cb(tick.last_price));
    const fullListeners = fullTickListeners.get(tick.instrument_token);
    if (fullListeners) fullListeners.forEach((cb) => cb(tick));
};

const sendMode = (mode, tokens) => {
    if (ws && ws.readyState === WebSocket.OPEN && tokens.length) {
        ws.send(JSON.stringify({ a: 'mode', v: [mode, tokens] }));
    }
};

const sendSubscribe = (tokens) => {
    if (ws && ws.readyState === WebSocket.OPEN && tokens.length) {
        ws.send(JSON.stringify({ a: 'subscribe', v: tokens }));
        ws.send(JSON.stringify({ a: 'mode', v: ['ltp', tokens] }));
    }
};

const sendUnsubscribe = (tokens) => {
    if (ws && ws.readyState === WebSocket.OPEN && tokens.length) {
        ws.send(JSON.stringify({ a: 'unsubscribe', v: tokens }));
    }
};

const scheduleReconnect = () => {
    if (reconnectTimer || manuallyClosed || authFailed) return;
    const delay = Math.min(1000 * 2 ** reconnectAttempts, 15000);
    reconnectAttempts += 1;
    reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        if (tokenRefCounts.size > 0) ensureConnection();
    }, delay);
};

function ensureConnection() {
    if (authFailed) return null;
    if (!isWithinTradingWindow()) return null;
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return connectPromise;
    // A connection attempt is already in flight (fetchSession() pending, `ws`
    // not assigned yet since that only happens once the fetch resolves).
    // Without this, N synchronous subscribeTick() calls in the same tick —
    // e.g. mapping over 40 strike contracts on first load — would each see
    // `ws` still null and independently kick off their own fetchSession() +
    // new WebSocket(), spawning dozens of parallel connection attempts
    // instead of sharing the one in flight.
    if (connectPromise) return connectPromise;

    manuallyClosed = false;
    connectPromise = fetchSession().then(({ api_key, access_token }) => {
        // Everything unsubscribed while the session fetch was in flight.
        if (tokenRefCounts.size === 0) { connectPromise = null; return; }
        const socket = new WebSocket(`${WS_URL}?api_key=${api_key}&access_token=${access_token}`);
        socket.binaryType = 'arraybuffer';

        socket.onopen = () => {
            reconnectAttempts = 0;
            const tokens = Array.from(tokenRefCounts.keys());
            if (tokens.length === 0) {
                manuallyClosed = true;
                socket.close();
                return;
            }
            sendSubscribe(tokens);
            sendMode('full', Array.from(fullTickListeners.keys()));
        };

        socket.onmessage = (event) => {
            if (!(event.data instanceof ArrayBuffer)) return;
            parseBinaryTicks(event.data).forEach(dispatchTick);
        };

        socket.onclose = (event) => {
            // Closed on purpose (closeForOutsideWindow already cleared `ws`) — a newer
            // connection may already be starting, so leave its state alone.
            if (ws !== socket) return;
            ws = null;
            connectPromise = null; // clear so a later ensureConnection() call can retry
            if (manuallyClosed) return;
            // Kite closes with 4xxx codes for auth/session problems — retrying
            // with the same (now-known-bad) token would just loop forever.
            if (event.code >= 4000 && event.code < 4100) {
                authFailed = true;
                console.error('Kite ticker auth failed, not retrying:', event.code, event.reason);
                return;
            }
            scheduleReconnect();
        };

        socket.onerror = () => { /* onclose fires right after with the real reason */ };

        ws = socket;
    }).catch((err) => {
        console.error('Kite ticker connection failed:', err);
        ws = null;
        connectPromise = null; // clear so scheduleReconnect's retry can actually reconnect
        scheduleReconnect();
    });

    return connectPromise;
}


function closeForOutsideWindow() {
    if (ws) {
        manuallyClosed = true; // stops onclose from scheduling a reconnect
        const socket = ws;
        ws = null;
        socket.close();
    }
    connectPromise = null;
    if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
    reconnectAttempts = 0;
}


setInterval(() => {
    if (authFailed) return;
    if (isWithinTradingWindow()) {
        if (!ws && !connectPromise && tokenRefCounts.size > 0) ensureConnection();
    } else if (ws) {
        closeForOutsideWindow();
    }
}, 30000);

let idleCloseTimer = null;
const IDLE_CLOSE_DELAY_MS = 3000;

const scheduleIdleClose = () => {
    if (idleCloseTimer) return;
    idleCloseTimer = setTimeout(() => {
        idleCloseTimer = null;
        if (tokenRefCounts.size === 0) closeForOutsideWindow();
    }, IDLE_CLOSE_DELAY_MS);
};

const cancelIdleClose = () => {
    if (idleCloseTimer) { clearTimeout(idleCloseTimer); idleCloseTimer = null; }
};

if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', closeForOutsideWindow);
}

export const subscribeTick = (instrumentToken, onPrice) => {
    if (!instrumentToken) return () => {};
    cancelIdleClose();
    ensureConnection();

    if (!tickListeners.has(instrumentToken)) tickListeners.set(instrumentToken, new Set());
    tickListeners.get(instrumentToken).add(onPrice);

    const prevCount = tokenRefCounts.get(instrumentToken) || 0;
    tokenRefCounts.set(instrumentToken, prevCount + 1);
    if (prevCount === 0) sendSubscribe([instrumentToken]);

    return () => {
        const listeners = tickListeners.get(instrumentToken);
        if (listeners) {
            listeners.delete(onPrice);
            if (listeners.size === 0) tickListeners.delete(instrumentToken);
        }
        const count = (tokenRefCounts.get(instrumentToken) || 1) - 1;
        if (count <= 0) {
            tokenRefCounts.delete(instrumentToken);
            sendUnsubscribe([instrumentToken]);
        } else {
            tokenRefCounts.set(instrumentToken, count);
        }
        if (tokenRefCounts.size === 0) scheduleIdleClose();
    };
};

export const subscribeFullTicks = (instrumentTokens, onTick) => {
    const tokens = Array.from(new Set((instrumentTokens || []).filter(Boolean)));
    if (tokens.length === 0) return () => {};
    cancelIdleClose();
    ensureConnection();

    const newTokens = [];
    tokens.forEach((token) => {
        if (!fullTickListeners.has(token)) fullTickListeners.set(token, new Set());
        fullTickListeners.get(token).add(onTick);
        const prevCount = tokenRefCounts.get(token) || 0;
        tokenRefCounts.set(token, prevCount + 1);
        if (prevCount === 0) newTokens.push(token);
    });
    if (ws && ws.readyState === WebSocket.OPEN && newTokens.length) {
        ws.send(JSON.stringify({ a: 'subscribe', v: newTokens }));
    }
    sendMode('full', tokens);

    return () => {
        const removed = [];
        const backToLtp = [];
        tokens.forEach((token) => {
            const listeners = fullTickListeners.get(token);
            if (listeners) {
                listeners.delete(onTick);
                if (listeners.size === 0) fullTickListeners.delete(token);
            }
            const count = (tokenRefCounts.get(token) || 1) - 1;
            if (count <= 0) {
                tokenRefCounts.delete(token);
                removed.push(token);
            } else {
                tokenRefCounts.set(token, count);
                if (!fullTickListeners.has(token)) backToLtp.push(token);
            }
        });
        sendUnsubscribe(removed);
        sendMode('ltp', backToLtp);
        if (tokenRefCounts.size === 0) scheduleIdleClose();
    };
};
